import React, { useState, useRef } from 'react';
import axios from 'axios';
import { ChevronRight } from '../../icons';
import Loading from '../../common_components/Loading';
import { DownloadInfoAlert } from './alerts/DownloadInfoAlert';
import { WRONG_FORMAT } from '../constants';
import { isCorrectFileFormat } from '../utils';
import { Modal, WarningModalBody, LoadingModalBody } from './Modal';

export const WelcomeScreen = ({
  file,
  setProjectData,
  setShowingWelcomeScreen,
  exampleDataLink,
  exampleInformationLink,
}) => {
  const [errorCode, setErrorCode] = useState();
  const [openedModal, setOpenedModal] = useState(null);

  const formRef = useRef();
  const modalRef = useRef();

  const handleFileChange = (event) => {
    setErrorCode(null);
    setOpenedModal('loading');
    modalRef?.current?.showModal();
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const file = formData.get('datafile');
      if (file && !isCorrectFileFormat(file)) {
        setErrorCode(WRONG_FORMAT);
        setOpenedModal('warning');
        event.target.value = '';
        return;
      }
      axios
        .post(window.DjUrls['ate:upload_file'](), formData)
        .then((res) => {
          setProjectData(res.data);
          modalRef?.current?.close();
          setShowingWelcomeScreen(false);
        })
        .catch((error) => {
          const data = error?.response?.data;
          const code = data?.details?.code;
          setErrorCode(code);
          setOpenedModal('warning');
          event.target.value = '';
        });
    } else {
      modalRef?.current?.close();
    }
  };

  return (
    <div className="text-center prose max-w-[--page-content-w] mx-auto w-full p-4 sm:p-8 lg:p-20 border-2 rounded-lg box-border border-neutral-200 shadow-lg bg-white relative">
      <div className="flex justify-between items-center">
        <h1 className="mb-0">Analizy Transformacji Energetycznej (ATE)</h1>
        <a
          href="https://www.youtube.com/watch?v=y1VHC0yU6Ic&list=PLsr7358AKbHrVSgDYDyxCuae8mXe5Risx&index=4"
          target="_blank"
          className="btn btn-outline btn-sm gap-2 whitespace-nowrap flex-shrink-0 shadow shadow-primary"
          rel="noopener noreferrer"
          title="Zobacz samouczek video"
        >
          <span
            className="hidden"
            dangerouslySetInnerHTML={{
              __html:
                '<!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.-->',
            }}
          ></span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 576 512"
            className="h-4 w-4"
            fill="currentColor"
          >
            <path d="M0 128C0 92.7 28.7 64 64 64l256 0c35.3 0 64 28.7 64 64l0 256c0 35.3-28.7 64-64 64L64 448c-35.3 0-64-28.7-64-64L0 128zM559.1 99.8c10.4 5.6 16.9 16.4 16.9 28.2l0 256c0 11.8-6.5 22.6-16.9 28.2s-23 5-32.9-1.6l-96-64L416 337.1l0-17.1 0-128 0-17.1 14.2-9.5 96-64c9.8-6.5 22.4-7.2 32.9-1.6z" />
          </svg>
          Samouczek
        </a>
      </div>
      <p className="text-left sm:text-justify mt-12">
        ATE to interaktywne narzędzie analityczne umożliwiające prowadzenie
        analiz zależności pomiędzy dowolnymi zestawami danych związanych z
        transformacją energetyczną. Użytkownicy mogą w prosty sposób obliczać
        współczynniki korelacji oraz regresji liniowej prostej, co pozwala na
        pełniejsze zrozumienie relacji pomiędzy zmiennymi opisującymi procesy
        energetyczne, środowiskowe czy gospodarcze. Jedną z kluczowych funkcji
        ATE jest automatyczne przekształcanie i standaryzacja danych z
        wykorzystaniem wielu metod statystycznych, dzięki czemu możliwe jest
        uzyskanie spójnych i porównywalnych wyników.
      </p>
      <p className="text-left sm:text-justify">
        Aplikacja oferuje interaktywne wizualizacje oraz moduł weryfikacji
        założeń modeli statystycznych, co zwiększa rzetelność analiz i ułatwia
        interpretację wyników także mniej doświadczonym użytkownikom.
      </p>

      <div className="divider my-8"></div>

      <h3 className="text-left mt-8 mb-4">Docelowa grupa użytkowników:</h3>
      <ul className="text-left list-disc list-inside space-y-2">
        <li>Administracja publiczna / Sektor publiczny</li>
        <li>
          Biznes / Firma / Przedsiębiorstwo (szczególnie sektor energetyczny,
          analityczny)
        </li>
        <li>Szkolnictwo / Edukacja (uczelnie, jednostki naukowe)</li>
        <li>Organizacje non-profit / NGO</li>
      </ul>

      <h3 className="text-left mt-8 mb-4">
        Najważniejsze korzyści dla użytkowników:
      </h3>
      <ul className="text-left list-disc list-inside space-y-2">
        <li>
          szybka i przejrzysta analiza zależności między danymi środowiskowymi,
          energetycznymi i gospodarczymi,
        </li>
        <li>
          możliwość obliczania korelacji i regresji bez konieczności pisania
          kodu,
        </li>
        <li>
          automatyczne przekształcanie danych za pomocą metod statystycznych
          zapewniających poprawność analizy,
        </li>
        <li>wykresy i wizualizacje wspomagające interpretację wyników,</li>
        <li>
          moduł weryfikacji założeń statystycznych, który zwiększa wiarygodność
          otrzymanych rezultatów,
        </li>
        <li>
          intuicyjny interfejs przyjazny zarówno ekspertom, jak i początkującym
          analitykom.
        </li>
      </ul>

      <h3 className="text-left mt-8 mb-4">Przykładowe zastosowanie:</h3>
      <p className="text-left sm:text-justify">
        ATE może być wykorzystane do badania zależności pomiędzy emisjami
        zanieczyszczeń a wskaźnikami produkcji energii, do analizy wpływu
        polityk energetycznych na efektywność sektora czy w procesach
        edukacyjnych, jako narzędzie wspierające naukę analizy danych.
      </p>

      <DownloadInfoAlert
        downloadLink={exampleDataLink}
        informationLink={exampleInformationLink}
      />
      {file ? (
        <button
          className="btn btn-lg btn-primary mt-8"
          onClick={() => setShowingWelcomeScreen(false)}
        >
          Ekran główny <ChevronRight className={'size-6'} />
        </button>
      ) : (
        <form ref={formRef}>
          <input
            type="file"
            className="file-input file-input-lg max-w-fit file-input-primary w-full mt-14"
            name="datafile"
            onChange={handleFileChange}
          />
        </form>
      )}
      <Modal ref={modalRef}>
        {openedModal === 'loading' ? (
          <LoadingModalBody />
        ) : openedModal === 'warning' ? (
          <WarningModalBody errorCode={errorCode} />
        ) : (
          <></>
        )}
      </Modal>
    </div>
  );
};
